import { FSAFarm, ResourceDetail } from '@cibo/core'
import { useSaveIndicator } from '@cibo/ui/src/components/SaveIndicator/SaveIndicatorProvider'
import Add from '@mui/icons-material/Add'
import { Autocomplete, ListItemIcon, ListItemText, MenuItem, Stack, TextField } from '@mui/material'
import { useDebounce } from '@react-hook/debounce'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { ChangeEventHandler, FocusEventHandler, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RemoveDetailRequest, SaveDetailRequest, useDetailEditing } from '../..'
import {
  useAssignFieldsToFSAFarm,
  useCreateFSAFarm,
  usePaginatedFSAFarms,
  useUnassignFieldsToFSAFarm,
} from '../../../queries/useFSAFarms'
import { PROGRAMS_QUERY_KEY } from '../../../types'

const answerFromDetail = (detail?: ResourceDetail) => {
  return detail?.result || ''
}

export const FarmInfoCellEditor = ({
  row,
  year = undefined,
  traitId = 'eqipEligibilityFarmInfo',
  disabled,
  ...rest
}: any) => {
  const { setIsDirty, setIsError, setIsSaving } = useSaveIndicator()
  const queryClient = useQueryClient()
  const detailEditing = useDetailEditing()
  const { t } = useTranslation('@cibo/landmanager/FarmInfo')
  const { enqueueSnackbar } = useSnackbar()
  const [fullText, setFullText] = useDebounce('', 250)
  const detail = row.resolveStandingDetail(traitId, year)
  const [fsaFarm, setFsaFarm] = useState(
    row.fsaFarm ? ({ ...row.fsaFarm, ...row.fsaFarm.item } as unknown as FSAFarm) : null
  )
  const [termsetError, setTermsetError] = useState<string>()
  const [tractNumber, setTractNumber] = useState(answerFromDetail(detail).tractNumber || '')
  const assignFields = useAssignFieldsToFSAFarm()
  const unassignFields = useUnassignFieldsToFSAFarm()
  const createFSAFarm = useCreateFSAFarm()
  const paginatedFarms = usePaginatedFSAFarms({
    fullText,
    owner: row.ownerId,
  })

  const saveDetail = (request: SaveDetailRequest, onSuccess?: (value?: any) => any) => {
    setIsSaving(true)
    setIsDirty(true)
    detailEditing
      .saveDetail(request)
      .then(() => {
        onSuccess && onSuccess()
        queryClient.invalidateQueries([PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS])
      })
      .catch(error => {
        setIsError(true)
        enqueueSnackbar(`${t('saveDetailError', { ns: '@cibo/landmanager/DetailEditing' })}`, {
          variant: 'error',
        })
      })
      .finally(() => setIsSaving(false))
  }

  const removeDetail = (request: RemoveDetailRequest, onSuccess?: (value?: any) => any) => {
    setIsSaving(true)
    setIsDirty(true)
    detailEditing.removeDetail(request).then(() => {
      onSuccess && onSuccess()
      queryClient
        .invalidateQueries([PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS])
        .catch(error => {
          setIsError(true)
          enqueueSnackbar(`${t('errorOnRemove', { ns: '@cibo/landmanager/DetailEditing' })}`, {
            variant: 'error',
          })
        })
        .finally(() => setIsSaving(false))
    })
  }

  useEffect(() => {
    setFsaFarm(row.fsaFarm ? ({ ...row.fsaFarm, ...row.fsaFarm.item } as unknown as FSAFarm) : null)
  }, [row])

  const assignFieldToFarm = (termset: FSAFarm) => {
    const request = {
      termsetId: termset.id,
      fieldIds: [row.resourceId],
    }
    assignFields
      .mutateAsync(request)
      .then((result: any) => {
        saveDetail(
          {
            resourceId: row.resourceId,
            details: [
              {
                traitId,
                year,
                result: { tractNumber, farmNumber: termset?.farmSerialNumber },
              },
            ],
          },
          () => {
            setTermsetError(undefined)
            enqueueSnackbar(t('successAssign', request), {
              variant: 'success',
            })
          }
        )
      })
      .catch(error => {
        setTermsetError(t('errorOnAssignShort'))
        enqueueSnackbar(`${t('errorOnAssign')}`, {
          variant: 'error',
        })

        throw error
      })
  }

  const unassignFieldToFarm = () => {
    if (!fsaFarm || !fsaFarm.id) return
    const request = {
      termsetId: fsaFarm.id,
      fieldIds: [row.resourceId],
    }
    unassignFields
      .mutateAsync(request)
      .then((result: any) => {
        saveDetail(
          {
            resourceId: row.resourceId,
            details: [{ traitId, year, result: { tractNumber } }],
          },
          () => {
            setTermsetError(undefined)
            enqueueSnackbar(t('successUnassign', request), {
              variant: 'success',
            })
          }
        )
      })
      .catch(error => {
        setTermsetError(t('errorOnUnassignShort'))
        enqueueSnackbar(t('errorOnUnassign'), {
          variant: 'error',
        })

        throw error
      })
  }

  const handleChangeFSAFarmNumber = (value: FSAFarm | string) => {
    if (typeof value === 'string') {
      const createRequest = {
        owner: row.ownerId,
        farmSerialNumber: value,
      }
      createFSAFarm
        .mutateAsync(createRequest)
        .then(a => {
          setTermsetError(undefined)
          setFsaFarm(a.item)
          assignFieldToFarm(a.item)
        })
        .catch(error => {
          setTermsetError(t('errorOnCreateShort'))
          enqueueSnackbar(t('errorOnCreate'), {
            variant: 'error',
          })
          throw error
        })
      return
    }
    setFsaFarm(value)
    assignFieldToFarm(value)
  }

  const handleChangeTractNumber: ChangeEventHandler<HTMLTextAreaElement> = event => {
    setTractNumber(event.target.value)
  }

  const handleSaveTractNumber: FocusEventHandler<HTMLTextAreaElement> = event => {
    const fsaFarmPresent = fsaFarm && typeof fsaFarm !== 'string'
    setTractNumber(event.target.value)
    const tractNumber = event.target.value
    if (tractNumber === '' && !fsaFarmPresent) {
      // removing entire detail
      removeDetail({
        resourceId: row.resourceId,
        traitId,
        year,
      })
    } else if (tractNumber === '' && fsaFarmPresent) {
      // saving only farm, not tract
      saveDetail({
        resourceId: row.resourceId,
        details: [
          {
            traitId,
            year,
            result: { farmNumber: fsaFarm.farmSerialNumber },
          },
        ],
      })
    } else if (tractNumber !== '' && !fsaFarmPresent) {
      // saving only tract, not farm
      saveDetail({
        resourceId: row.resourceId,
        details: [
          {
            traitId,
            year,
            result: { tractNumber },
          },
        ],
      })
    } else {
      // saving farm and tract
      saveDetail({
        resourceId: row.resourceId,
        details: [
          {
            traitId,
            year,
            result: { tractNumber, farmNumber: fsaFarm?.farmSerialNumber },
          },
        ],
      })
    }
  }

  return (
    <Stack direction="row" spacing={3} sx={{ width: '100%' }}>
      <Autocomplete<FSAFarm, false, false, true>
        id="fsa-farm-termset"
        freeSolo
        getOptionLabel={(option: FSAFarm | string) =>
          typeof option === 'string' ? option : option.farmSerialNumber
        }
        sx={{ flex: 1 }}
        value={fsaFarm}
        disabled={createFSAFarm.isLoading}
        options={[...(paginatedFarms.data?.items || [])]}
        onInputChange={(_, value) => setFullText(value)}
        renderOption={(props, option) => {
          if (typeof option === 'string') {
            return (
              <MenuItem key={'create'} {...props}>
                <ListItemIcon>
                  <Add color="secondary" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ color: 'secondary' }}>
                  {fullText === '' ? t('addNewFarmNumber') : t('addFarmWithNumber', { fullText })}
                </ListItemText>
              </MenuItem>
            )
          }
          return (
            <MenuItem key={option.id} {...props}>
              {option.farmSerialNumber}
            </MenuItem>
          )
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={t('farmNumber')}
            placeholder={t('selectFarmNumber')}
            variant="outlined"
            error={!!termsetError}
            helperText={termsetError}
            onBlur={event => {
              if (!event.target.value) {
                unassignFieldToFarm()
              }
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}
        onChange={(event, value) => {
          if (!value) {
            event.type === 'click' && unassignFieldToFarm()
            return
          } else {
            handleChangeFSAFarmNumber(value)
          }
        }}
        filterOptions={(options, params) => {
          const isExisting = options.some(option => fullText === option.farmSerialNumber)
          if (fullText !== '' && !isExisting) {
            // @ts-ignore options can be strings, mui makes you respect strings
            options.push(fullText)
          }
          if (fullText === '') {
            // @ts-ignore options can be strings, mui makes you respect strings
            options.push('')
          }

          return options
        }}
      />
      <TextField
        value={tractNumber}
        label={t('tractNumber')}
        placeholder={t('enterTractNumber')}
        variant="outlined"
        disabled={disabled}
        onChange={handleChangeTractNumber}
        onBlur={handleSaveTractNumber}
        data-testid={`farminfo_editor2`}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{ flex: 1 }}
      />
    </Stack>
  )
}
